import { forwardRef, useImperativeHandle } from 'react';
import { useToggle } from '@alluradirect/react';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import Grid from 'components/core/Grid';
import { AlluraErrorMessage } from 'components/formik/core/AlluraErrorMessage/AlluraErrorMessage';
import { BookingSettingsDates } from 'components/views/pages/Checkout/CheckoutStep1/components/BookingSettingsDates';
import { BookingSettingsGuestDialog } from 'components/views/pages/Checkout/CheckoutStep1/components/BookingSettingsGuestDialog';
import { BookingSettingsItem } from 'components/views/pages/Checkout/CheckoutStep1/components/BookingSettingsItem';
import { BookingSettingsPetsDialog } from 'components/views/pages/Checkout/CheckoutStep1/components/BookingSettingsPetsDialog';

import { generateGuestsDisplay, generatePetsDisplay } from 'components/views/pages/Checkout/helpers';
import { CheckoutStepProps } from 'pages/[propertyId]/reservenow';
import { useMemo } from 'react';
import { CheckoutBoxWrapper } from '../../styles';

type Props = Pick<
  CheckoutStepProps,
  'adults' | 'pChildren' | 'infants' | 'pets' | 'startDate' | 'endDate' | 'isInvalidDates'
> & {
  isPetFriendly: boolean | undefined;
  maxPartySize: number | undefined;
  isLoading: boolean;
  guestTitle?: string | undefined;
  guestPlaceholder?: string | undefined;
  dateTitle?: string | undefined;
  datePlaceholder?: string | undefined;
  onDatesClose?: () => void;
  currency_code: string;
  currency_multiplier: number;
};

export const BookingSettings = forwardRef((props: Props, ref: any) => {
  const _datesToggle = useToggle(),
    datesToggle = {
      ..._datesToggle,
      close: () => {
        props.onDatesClose?.();
        _datesToggle.close();
      },
    };
  const guestsToggle = useToggle();
  const petsToggle = useToggle();

  useImperativeHandle(ref, () => ({
    datesToggle,
    guestsToggle,
  }));

  const guestPlaceholder =
    props.maxPartySize === undefined ? 'Select Dates (EDIT) to Continue' : props.guestPlaceholder;
  const guestsDisplay = generateGuestsDisplay(
    {
      adults: props.adults,
      children: props.pChildren,
      infants: props.infants,
    },
    guestPlaceholder,
  );

  const hasPets = useMemo(() => {
    if (!props.isPetFriendly) return false;
    return props.isPetFriendly;
  }, [props.isPetFriendly]);

  const petsDisplay = generatePetsDisplay({
    pets: props.pets,
    allowsPets: hasPets,
  });

  const guestsNotSet = props.adults === undefined;
  // did not work on contact page - not necessarily dates
  // const openGuestModal = props.startDate && props.endDate && !datesToggle.isOpen && (guestsToggle.isOpen || guestsNotSet);
  const openGuestModal = guestsToggle.isOpen;
  return (
    <CheckoutBoxWrapper>
      <Grid item xs={12}>
        <BookingSettingsDates
          startDate={props.startDate}
          endDate={props.endDate}
          isInvalidDates={props.isInvalidDates}
          title={props.dateTitle}
          placeholder={props.datePlaceholder}
          datesToggle={datesToggle}
          currency_code={props.currency_code}
          currency_multiplier={props.currency_multiplier}
        />
        {props.isInvalidDates ? (
          <Grid item xs={12}>
            <AlluraErrorMessage>These dates are no longer available</AlluraErrorMessage>
          </Grid>
        ) : (
          <>
            {props.maxPartySize === undefined ? null : (
              <>
                <AppDivider borderWidth="0" />
                <BookingSettingsItem
                  title={props.guestTitle ?? 'Guests'}
                  value={guestsDisplay}
                  onClick={guestsToggle.open}
                  isHighlighted={guestsNotSet}
                  buttonProps={{
                    disabled: props.maxPartySize === undefined,
                  }}
                />
              </>
            )}
            <AppDivider borderWidth="0" />
            <BookingSettingsItem
              title="Pets"
              value={petsDisplay}
              onClick={petsToggle.open}
              disabled={props.isLoading || !hasPets}
              isLoading={props.isLoading}
            />
          </>
        )}
      </Grid>

      {/* Edit Guests Modal */}
      {openGuestModal ? (
        <BookingSettingsGuestDialog
          isOpen={openGuestModal}
          close={guestsToggle.close}
          maxPartySize={props.maxPartySize}
          adults={props.adults}
          pChildren={props.pChildren}
          infants={props.infants}
          isLoading={props.isLoading}
          hasHeaderCancelButton={false}
        />
      ) : null}

      {/* Edit Pets Modal */}
      {petsToggle.isOpen ? (
        <BookingSettingsPetsDialog pets={props.pets} isOpen={petsToggle.isOpen} close={petsToggle.close} />
      ) : null}
    </CheckoutBoxWrapper>
  );
});
